import { useCoreClient } from '@hooks/core-client';
import { notificationErrorState, notificationSuccessState } from '@states';
import { fileToBase64 } from '@utils/transform';
import axios from 'axios';
import { useState } from 'react';
import { useRecoilState } from 'recoil';
import * as Sentry from '@sentry/browser';
import { useCoreClientSafe } from '@utils/core-client';

const defaultLogoUploadErrorMessage = 'There was an issue uploading your logo';

interface CloudinaryUploadResponse {
  public_id: string;
  secure_url: string;
}

export const useCloudinaryFileUploader = () => {
  const [loading, setLoading] = useState(false);
  const [, setNotificationSuccess] = useRecoilState(notificationSuccessState);
  const [, setNotificationError] = useRecoilState(notificationErrorState);
  const coreClient = useCoreClient();
  const coreClientSafe = useCoreClientSafe();

  const uploadFileToCloudinary = async (
    file: File,
  ): Promise<CloudinaryUploadResponse | undefined> => {
    setLoading(true);

    let response: CloudinaryUploadResponse | undefined;

    try {
      const base64EncodedFile = await fileToBase64(file);

      if (!base64EncodedFile) {
        setNotificationError(defaultLogoUploadErrorMessage);
        setLoading(false);
        return response;
      }

      const timestamp = Date.now();

      const uploadRequestSignature = await coreClientSafe({
        args: {
          fileUploadSignatureRequestCreateDto: {
            folder: `${GIVZ_ENV}/merchants`,
            timestamp,
          },
        },
        fn: coreClient().FileUploads.createUploadSignature,
      });

      if (uploadRequestSignature.errors) {
        setNotificationError(uploadRequestSignature.errors);
      } else {
        const uploadFormData = new FormData();
        uploadFormData.append('file', base64EncodedFile);
        uploadFormData.append('upload_preset', 'ml_default');
        uploadFormData.append('folder', `${GIVZ_ENV}/merchants`);
        uploadFormData.append('api_key', CLOUDINARY_API_KEY);
        uploadFormData.append('timestamp', timestamp.toString());
        uploadFormData.append(
          'signature',
          uploadRequestSignature.data.signature,
        );

        const uploadedFile = await axios.post<CloudinaryUploadResponse>(
          `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
          uploadFormData,
        );

        setNotificationSuccess('File uploaded successfully');
        setLoading(false);

        response = uploadedFile.data;
      }
    } catch (error) {
      Sentry.captureException(error);
      setNotificationError(defaultLogoUploadErrorMessage);
    }

    setLoading(false);
    return response;
  };

  return {
    fileUploadLoading: loading,
    uploadFileToCloudinary,
  };
};
